import React from "react";
import { View, ScrollView } from "react-native";
import { BettingEventStatus, FreeplayLeague } from "@freeplay/shared-code";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { ButtonSize, ButtonTheme, RoundedButton } from "../components/buttons/roundedButton/RoundedButton";
import { GlobalStyles } from "../components/style/Style";
import { useServices } from "../contexts/ServiceContext";
import { GameTitle } from "../components/picks/GameTitle";
import { MediaQueries } from "../components/hooks/dynamicStyleHook";
import { useAgreements } from "../components/hooks/agreementsHook";
import { BespokeSelectionContainer } from "../components/picks/BespokeSelectionContainer";
import { BespokeGameActions, BespokeGameState } from "../controllers/BespokeGameController";
import { format } from "date-fns";
import { useEvent } from "../contexts/EventContext";
import DefaultImage from "../assets/default_background.png";
import DefaultTSImage from "../assets/default_background_ts.png";
import { CountDownTimer } from "../components/textcontainers/CountDownTimer";
import { MixPanelEventEnum } from "../utils/analytics";
import mixpanel from "mixpanel-browser";

export interface GameViewProps {
    testID?: string;
    state: BespokeGameState;
    dispatch: React.Dispatch<BespokeGameActions>;
    sendPicks: () => void;
}

export const BespokeGameView: React.FC<GameViewProps> = ({ testID, state, dispatch, sendPicks }) => {
    const { edgeDeploy, fpUser } = useServices();
    const { currentEvent, reviewPicksState, setReviewState } = useEvent();
    const { allAgreementsAccepted, agreementsTag } = useAgreements();
    const { choices } = state;
    const styles = useDynamicStyles(generateStyles);
    const hideButtons =
        currentEvent?.eventStatus === BettingEventStatus.BETTING_CLOSED ||
        currentEvent?.eventStatus === BettingEventStatus.PENDING_SCORING;

    if (!currentEvent) {
        return <></>;
    }

    const submitButtonText = reviewPicksState ? `${fpUser ? "" : "Login & "}SUBMIT PICKS` : "REVIEW PICKS";
    const { bespokePrompts } = currentEvent;

    const handleSubmitButton = () => {
        if (reviewPicksState) {
            sendPicks();
        } else {
            toggleReviewState();
        }
    };
    const toggleReviewState = async () => {
        if (!reviewPicksState) {
            mixpanel.track(MixPanelEventEnum.REVIEW_PICKS_SCREEN_VIEWED, {
                freeplay_event_id: currentEvent.id,
            });
        }
        setReviewState(!reviewPicksState);
    };

    const optional = currentEvent.prizeDescription || "Make your choices and press submit!";

    const ContainerView = edgeDeploy ? View : ScrollView;
    return (
        <View style={edgeDeploy ? styles.edgeContainer : styles.container}>
            <GameTitle
                title={currentEvent.name}
                date={format(new Date(currentEvent.bettingEnds), "LLLL d, h:mm b")}
                optionalText={optional}
                backgroundImage={currentEvent.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage}
            />
            {!reviewPicksState && !hideButtons && <CountDownTimer inDate={currentEvent.bettingEnds} />}
            <ContainerView style={styles.selectionContainer}>
                <View style={[styles.choiceContainer, edgeDeploy ? styles.edgeChoiceContainer : {}]}>
                    {bespokePrompts.map((prompt, index) => (
                        <BespokeSelectionContainer
                            key={`bespoke-selection-container-${prompt.id}`}
                            prompt={prompt}
                            onSelect={(choice) => dispatch({ type: "setSelection", choice, index })}
                            currentPick={choices[index]}
                            disabled={hideButtons}
                        />
                    ))}
                </View>
            </ContainerView>
            <View
                style={[
                    styles.bottomButtonsContainer,
                    edgeDeploy && styles.edgeBottomButtonsContainer,
                    reviewPicksState && styles.hasTerms,
                ]}
            >
                {reviewPicksState && agreementsTag}
                <RoundedButton
                    config={{
                        buttonText: submitButtonText,
                        size: ButtonSize.LARGE,
                        width: edgeDeploy ? 300 : 250,
                        height: edgeDeploy ? 60 : 50,
                        theme: ButtonTheme.ACCENTED,
                        disabled: reviewPicksState
                            ? !allAgreementsAccepted
                            : !(state.choices.length === bespokePrompts.length),
                        margin: { left: reviewPicksState ? 10 : 0 },
                    }}
                    testID={`next-game-button`}
                    onPress={handleSubmitButton}
                />
            </View>
        </View>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
            edgeMiddleButton: {
                marginHorizontal: "10px",
            },
            edgeChoiceContainer: {
                paddingHorizontal: 0,
            },
        },
        [MediaQueries.SMALL]: {
            edgeBottomButtonsContainer: {
                height: 90,
            },
        },
        middleButton: {
            marginHorizontal: "10px",
        },
        edgeMiddleButton: {
            marginHorizontal: "30px",
        },
        edgeContainer: {
            width: "100%",
            maxWidth: 860,
            marginHorizontal: "auto",
            padding: 30,
            marginVertical: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            flex: 1,
            alignItems: "center",
        },
        container: {
            flex: 1,
            alignItems: "center",
        },

        buttonContainer: {
            width: "100%",
            flexDirection: "row",
            paddingHorizontal: 20,
        },
        edgeButtonContainer: {
            paddingHorizontal: 0,
        },
        selectionContainer: {
            flex: 1,
            flexDirection: "column",
            alignSelf: "stretch",
        },
        bottomButtonsContainer: {
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.5,
            shadowRadius: 16,
            shadowColor: () => EStyleSheet.value(GlobalStyles.DEFAULT_SHADOW),
            width: "100%",
            height: 70,
            paddingTop: 10,
            paddingHorizontal: 10,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.GAME_CTA_BACKGROUND_COLOUR),
        },
        edgeBottomButtonsContainer: {
            height: 100,
            paddingHorizontal: 0,
        },
        hasTerms: {
            height: 180,
        },
        choiceContainer: {
            justifyContent: "center",
            paddingHorizontal: 20,
        },
        edgeChoiceContainer: {},
    });
};
